import type { ProTheme } from "@/stores/pro-theme";

/** Matches `--pro-bg` in `pro-tokens.css` (dark default). */
export const PRO_DOCUMENT_CHROME_DARK = "#0c0c0f";

/** Matches `--pro-bg` in `.pro-light`. */
const PRO_DOCUMENT_CHROME_LIGHT = "#ecedf0";

/** Matches `--pro-bg` in `.pro-light[data-pro-surface="mobile"]`. */
export const PRO_DOCUMENT_CHROME_LIGHT_MOBILE = "#ffffff";

/** Consumer default (`--paper` in tokens.css). */
const CONSUMER_DOCUMENT_CHROME = "#fafaf7";

function proDocumentChromeColor(theme: ProTheme, isMobileCanvas: boolean): string {
  if (theme === "light") {
    return isMobileCanvas ? PRO_DOCUMENT_CHROME_LIGHT_MOBILE : PRO_DOCUMENT_CHROME_LIGHT;
  }
  return PRO_DOCUMENT_CHROME_DARK;
}

function setThemeColorMeta(content: string): void {
  // Replace rather than mutate — iOS Safari often ignores in-place theme-color updates.
  for (const meta of document.querySelectorAll('meta[name="theme-color"]')) {
    meta.remove();
  }
  const meta = document.createElement("meta");
  meta.name = "theme-color";
  meta.content = content;
  document.head.prepend(meta);
}

function isProDashboardRoute(): boolean {
  if (typeof window === "undefined") return false;
  return /^\/dashboard(\/|$)/.test(window.location.pathname);
}

function isProMobileCanvas(): boolean {
  if (typeof window === "undefined") return false;
  return !window.matchMedia("(min-width: 768px)").matches;
}

/** Sync html/body chrome when theme changes on a dashboard route (call outside React too). */
export function syncProDocumentChromeForTheme(theme: ProTheme): void {
  if (!isProDashboardRoute()) return;
  applyProDocumentChrome(theme, isProMobileCanvas());
}

function paintDocumentChromeSurfaces(color: string, isMobileCanvas: boolean): void {
  const html = document.documentElement;
  html.style.backgroundColor = color;
  if (document.body) {
    document.body.style.backgroundColor = color;
  }
  const root = document.getElementById("root");
  if (root) {
    // Mobile dashboard shell is fixed from viewport top; keep #root transparent so
    // Safari 26+ samples the shell's bg-pro-bg (updates with pro-light/pro-dark).
    root.style.backgroundColor = isMobileCanvas ? "transparent" : color;
  }
}

function clearDocumentChromeSurfaceStyles(): void {
  const html = document.documentElement;
  html.style.backgroundColor = "";
  if (document.body) {
    document.body.style.backgroundColor = "";
  }
  const root = document.getElementById("root");
  if (root) {
    root.style.backgroundColor = "";
  }
}

/** Paint html/body + browser chrome for pro dashboard (status bar, overscroll). */
export function applyProDocumentChrome(theme: ProTheme, isMobileCanvas: boolean): void {
  const html = document.documentElement;
  const color = proDocumentChromeColor(theme, isMobileCanvas);
  html.dataset.appSurface = "pro";
  html.dataset.proTheme = theme;
  if (isMobileCanvas) {
    html.dataset.proMobile = "true";
  } else {
    html.removeAttribute("data-pro-mobile");
  }
  html.style.colorScheme = theme;
  paintDocumentChromeSurfaces(color, isMobileCanvas);
  setThemeColorMeta(color);
}

/** Restore consumer shell colors when leaving `/dashboard`. */
export function resetDocumentChrome(): void {
  const html = document.documentElement;
  html.removeAttribute("data-app-surface");
  html.removeAttribute("data-pro-theme");
  html.removeAttribute("data-pro-mobile");
  html.style.colorScheme = "";
  clearDocumentChromeSurfaceStyles();
  setThemeColorMeta(CONSUMER_DOCUMENT_CHROME);
}
